import { useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
	RovoConfiguration,
	RovoConfigurationProviderProps,
	SearchPageRovoConfigProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import {
	type GetSearchPageRovoActions,
	useSearchPageRovoActions,
} from '@confluence/search-page-rovo-config-common/entry-points/useSearchPageRovoActions.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import iconDark from './assets/search-icon-colorized-dark.svg';
import iconLight from './assets/search-icon-colorized-light.svg';

const messages = defineMessages({
	searchAssistanceGreetingTitle: {
		id: 'advanced-search.rovo-config-provider.search-assistance-greeting-title',
		defaultMessage: 'Can I help you find something{name, select, undefined {} other {, {name}}}?',
		description: 'Title of the search assistance greeting.',
	},
});

const EMPTY_ROVO_CONFIG: RovoConfiguration = {
	getActions: async () => [],
};

export const AdvancedSearchRovoConfigProvider = ({
	setConfiguration,
}: RovoConfigurationProviderProps) => {
	const props = useRovoConfigProviderProps() as SearchPageRovoConfigProviderProps;
	const { getSearchQuery, shouldUseSearchPageRovoConfig } = props;
	const query = (getSearchQuery?.() ?? '').toString();
	const getSearchPageRovoActions: GetSearchPageRovoActions = useSearchPageRovoActions(query);
	const { displayName } = useSessionData();

	const intl = useIntl();

	// Use first name for greeting when available; fallback for anonymous or missing displayName
	const firstName = displayName?.split(' ')[0];
	const title = intl.formatMessage(messages.searchAssistanceGreetingTitle, {
		name: firstName ?? undefined,
	});

	// When undefined (e.g. prop not passed), use empty config for backward compatibility
	const useSearchPageConfig = shouldUseSearchPageRovoConfig ?? false;

	const config = useMemo<RovoConfiguration>(
		() =>
			useSearchPageConfig && query !== ''
				? {
						getActions: async () => {
							const actionEntries = getSearchPageRovoActions();
							const actions = actionEntries.map(({ action }) => action);
							return Promise.resolve(actions);
						},
						customGreeting: {
							title,
							iconSrc: iconLight,
							iconSrcDark: iconDark,
							truncateGreeting: false,
						},
						prioritizeCustomActionResults: true,
						shouldShowGreeting: true,
					}
				: EMPTY_ROVO_CONFIG,
		[useSearchPageConfig, getSearchPageRovoActions, title, query],
	);

	useEffect(() => {
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};
